/* eslint-disable @typescript-eslint/indent */
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import * as moment from "moment";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { PaginatedResponse } from "src/app/core/http/paginated-response.type";
import { DropdownOption, DropdownQuestion } from "src/app/shared/components/dynamic-form/question-dropdown";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DateQuestion } from "../../../../shared/components/dynamic-form/question-date";
import { TextAreaQuestion } from "../../../../shared/components/dynamic-form/question-textarea";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { FORMAT } from "../../../../shared/constants/formats";
import { pattern } from "../../../../shared/constants/patterns";
import { UserInfo } from "../../../../shared/types/user-info";
import { isNotEqual, isSameOrAfter } from "../../../../shared/validators/validators";
import { MaintenanceProviderService } from "../../maintenance-provider/maintenance-provider.service";
import { MaintenanceProvider } from "../../maintenance-provider/maintenance-provider.types";
import { Contract } from "../contract.type";

@Injectable()
export class RenewContractService {
  constructor(private maintenanceProviderService: MaintenanceProviderService) {}

  generateQuestionsForCurrentRenewal(options: {
    contract?: Contract;
    currentUser: UserInfo;
    maintenanceProviderValue?: MaintenanceProvider;
  }): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        value: options.contract?.name,
        key: "nameCurrent",
        label: "Current Contract Name",
        order: 1,
        disabled: true,
      }),
      new TextboxQuestion({
        value: `${options.contract.number}-${options.contract.version}`,
        key: "contractNumber",
        label: "Current Contract #",
        order: 2,
        disabled: true,
      }),
      new DateQuestion({
        value: options.contract?.startDate,
        key: "startDateCurrent",
        label: "Current Start Date",
        disabled: true,
        order: 3,
      }),
      new DateQuestion({
        value: options.contract?.endDate,
        key: "endDateCurrent",
        label: "Current End Date",
        disabled: true,
        order: 4,
      }),
      new DropdownQuestion({
        value: options.contract?.maintenanceProviderId,
        key: "maintenanceProviderId",
        label: "Maintenance Provider",
        disabled: true,
        required: true,
        tooltipEnabled: true,
        currentOption: {
          label: options.contract?.maintenanceProvider?.name,
          value: options.contract?.maintenanceProvider?.id,
        },
        asyncOptions: this.getMaintenanceProviders({}, options.currentUser),
        dependsOn: {
          key: "partnerId",
          callback: ({ parentValue: partnerId }) => {
            return this.getMaintenanceProviders({ partnerId }, options.currentUser);
          },
        },
        search: {
          filter: {
            parentKey: "partnerId",
            callback: ({ value: name, parentValue: partnerId }) => {
              return this.getMaintenanceProviders({ name, partnerId }, options.currentUser);
            },
          },
        },
        order: 5,
      }),
      new TextboxQuestion({
        value: options.contract?.sfOpportunity,
        key: "sfOpportunityCurrent",
        label: "Current Salesforce Opportunity",
        disabled: true,
        order: 6,
      }),
      new TextAreaQuestion({
        value: options.contract?.notes,
        key: "notesCurrent",
        label: "Current Internal Notes",
        disabled: true,
        order: 7,
      }),
      new TextAreaQuestion({
        value: options.contract?.configurationNotes,
        key: "configurationNotesCurrent",
        label: "Current Configuration Notes",
        disabled: true,
        order: 8,
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  generateQuestionsForNewRenewal(options: {
    contract?: Contract;
    currentUser: UserInfo;
    maintenanceProviderValue?: MaintenanceProvider;
  }): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        key: "name",
        label: "New Contract Name",
        value: "",
        required: true,
        order: 1,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "New Contract Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(2),
            message: "New Contract Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(200),
            message: "New Contract Name is <strong>too long</strong>",
          },
          {
            key: "same",
            validator: isNotEqual(options.contract?.name),
            message: "New Contract Name <strong>can't be the same</strong>",
          },
        ],
      }),
      new TextboxQuestion({
        value: `${options.contract.number}-${options.contract.version + 1}`,
        key: "contractNumber",
        label: "New Contract #",
        order: 2,
        disabled: true,
      }),
      new DateQuestion({
        value: this.getNewStartDate(options.contract?.endDate),
        key: "startDate",
        label: "New Start Date",
        required: true,
        minDate: new Date(this.getNewStartDate(options.contract?.endDate)),
        order: 3,
        errors: [],
      }),
      new DateQuestion({
        key: "endDate",
        label: "New End Date",
        required: true,
        value: undefined,
        minDate: new Date(this.getNewStartDate(options.contract?.endDate)),
        order: 4,
        errors: [
          {
            key: "minDate",
            validator: isSameOrAfter(options.contract?.endDate),
            message: `New End Date <strong>can't be lower than ${moment(options.contract?.endDate)
              .utc()
              .format(FORMAT.DATE)}</strong>`,
          },
        ],
      }),
      new DropdownQuestion({
        value: options.contract?.maintenanceProviderId,
        key: "maintenanceProviderId",
        label: "New Maintenance Provider",
        required: true,
        tooltipEnabled: true,
        currentOption: {
          label: options.contract?.maintenanceProvider?.name,
          value: options.contract?.maintenanceProvider?.id,
        },
        asyncOptions: this.getMaintenanceProviders({}, options.currentUser),
        dependsOn: {
          key: "partnerId",
          callback: ({ parentValue: partnerId }) => {
            return this.getMaintenanceProviders({ partnerId }, options.currentUser);
          },
        },
        search: {
          filter: {
            parentKey: "partnerId",
            callback: ({ value: name, parentValue: partnerId }) => {
              return this.getMaintenanceProviders({ name, partnerId }, options.currentUser);
            },
          },
        },
        order: 5,
      }),
      new TextboxQuestion({
        key: "sfOpportunity",
        label: "New Salesforce Opportunity",
        value: "",
        required: false,
        order: 6,
        errors: [
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "New Salesforce Opportunity is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(600),
            message: "New Salesforce Opportunity is <strong>too long</strong>",
          },
        ],
      }),
      new TextAreaQuestion({
        key: "notes",
        label: "New Internal Notes",
        required: false,
        value: "",
        order: 7,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "New Notes is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "New Notes is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(1000),
            message: "New Notes is <strong>too long</strong>",
          },
        ],
      }),
      new TextAreaQuestion({
        key: "configurationNotes",
        label: "New Configuration Notes",
        required: false,
        value: "",
        order: 8,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "New Configuration Notes is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "New Configuration Notes is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(1000),
            message: "New Configuration Notes is <strong>too long</strong>",
          },
        ],
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  generateQuestionsForSummaryRenewal(options: {
    name: string;
    newNumberVersion: string;
    startDate: string;
    endDate: string;
    renewedAssetsCount: string;
    newAssetsCount: string;
    deletedAssetsCount: string;
    sfOpportunity?: string;
    notes?: string;
    configurationNotes?: string;
    totalPreviousPrice?: string;
    totalPrice?: string;
    maintenanceProvider?: string;
  }): Observable<QuestionBase<string | boolean | number>[]> {
    const questions: QuestionBase<string | boolean | number>[] = [
      new TextboxQuestion({
        value: options.name,
        key: "contractName",
        label: "Contract Name",
        disabled: true,
        order: 1,
      }),
      new TextboxQuestion({
        value: options.newNumberVersion,
        key: "contractNumber",
        label: "Contract #",
        disabled: true,
        order: 2,
      }),
      new DateQuestion({
        value: options.startDate,
        key: "startDate",
        label: "Start Date",
        disabled: true,
        order: 3,
      }),
      new DateQuestion({
        value: options.endDate,
        key: "endDate",
        label: "End Date",
        disabled: true,
        order: 4,
      }),
      new TextboxQuestion({
        value: options.maintenanceProvider,
        key: "maintenanceProvider",
        label: "Maintenance Provider",
        disabled: true,
        order: 5,
      }),
      new TextboxQuestion({
        value: options.renewedAssetsCount,
        key: "existingAssets",
        label: "Assets to be Renewed",
        disabled: true,
        order: 6,
      }),
      new TextboxQuestion({
        value: options.newAssetsCount,
        key: "newAssets",
        label: "Assets to be Added",
        disabled: true,
        order: 7,
      }),
      new TextboxQuestion({
        value: options.deletedAssetsCount,
        key: "deletedAssets",
        label: "Assets not to be Renewed",
        disabled: true,
        order: 8,
      }),
      new TextboxQuestion({
        value: options.sfOpportunity,
        key: "sfOpportunity",
        label: "Salesforce Opportunity",
        disabled: true,
        order: 9,
      }),
      new TextboxQuestion({
        value: options.totalPreviousPrice,
        key: "totalPreviousPrice",
        label: "Total Previous Price",
        disabled: true,
        order: 10,
      }),
      new TextboxQuestion({
        value: options.totalPrice,
        key: "totalPrice",
        label: "Total Price",
        disabled: true,
        order: 11,
      }),
      new TextAreaQuestion({
        value: options.notes,
        key: "notes",
        label: "Internal Notes",
        disabled: true,
        order: 12,
      }),
      new TextAreaQuestion({
        value: options.configurationNotes,
        key: "configurationNotes",
        label: "Configuration Notes",
        disabled: true,
        order: 13,
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  /**
   * Get the new start date for the contract/asset to be renewed
   * @param date end date of the current contract/asset
   * @returns
   */
  getNewStartDate(date: string): string {
    // The new start date should be the current end date plus one day
    return moment(date).add(1, "day").toISOString();
  }

  getMaintenanceProviders(
    query: { name?: string; partnerId?: string },
    currentUser: UserInfo,
  ): Observable<DropdownOption[]> {
    const partnerId = query?.partnerId || currentUser?.partnerId || currentUser?.company?.partnerId;
    return this.maintenanceProviderService
      .getMaintenanceProviders({
        relations: { partners: true },
        joinType: "LEFT",
        ...(currentUser.isGlobal() && {
          where: [
            {
              isDefault: true,
              name: { $cont: query?.name },
            },
            {
              partners: { id: partnerId },
              name: { $cont: query?.name },
            },
          ],
        }),
        // if user is not global, then it only can retrieve non channelworks maintenance providers
        ...(!currentUser.isGlobal() && {
          where: [
            {
              isChannelWorks: false,
              name: { $cont: query?.name },
            },
          ],
        }),
      })
      .pipe(
        map((data: PaginatedResponse<MaintenanceProvider>) =>
          data.items.map((c: MaintenanceProvider) => ({ value: c.id, label: c.name })),
        ),
      );
  }
}
